import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User } from '@/types/models.types'
import { clearUserScopedStorage } from './sessionStorageCleanup'

const USER_KEY = 'bca_user'
const TOKEN_KEY = 'bca_token'
const TIME_ZONE_KEY = 'bca_time_zone'

function loadUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_KEY)
    return raw ? (JSON.parse(raw) as User) : null
  } catch {
    return null
  }
}

/**
 * Dice se un token è scaduto leggendone la scadenza (`exp`), senza chiedere niente al server.
 *
 * Il client NON verifica la firma, e non deve: la chiave sta solo nell'API. Qui si legge una
 * data che il server ha scritto, per una ragione sola — sapere PRIMA di disegnare una pagina
 * che la sessione è finita. Senza, la guardia del router vedeva un token in localStorage e
 * lasciava passare; l'app si montava, la prima lettura tornava 401 e solo allora si
 * veniva portati al login: un istante di pagina di amministrazione mostrato a chi non è più
 * dentro.
 *
 * Un token che non si riesce a leggere conta come scaduto: non è un JWT, quindi l'API lo
 * rifiuterebbe comunque. Uno senza `exp` invece passa, e decide il server.
 */
export function isTokenExpired(token: string, now: number = Date.now()): boolean {
  try {
    const payload = token.split('.')[1]
    if (!payload) return true
    // Il payload di un JWT è base64url, `atob` vuole base64 classico.
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const exp = (JSON.parse(json) as { exp?: unknown }).exp
    if (typeof exp !== 'number') return false
    // `exp` è in secondi, `Date.now()` in millisecondi.
    return exp * 1000 <= now
  } catch {
    return true
  }
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(loadUser())
  const token = ref<string | null>(localStorage.getItem(TOKEN_KEY))
  /**
   * Fuso EFFETTIVO in cui rendere gli istanti, già risolto dal server: il client non
   * ricalcola nessuna precedenza. Persistito come token e utente perché al reload della
   * pagina non c'è un login da cui rileggerlo, e senza valore le date tornerebbero al fuso
   * del browser. `null` = fuso del browser.
   */
  const timeZone = ref<string | null>(localStorage.getItem(TIME_ZONE_KEY))

  const isAuthenticated = computed(() => token.value !== null)

  function setToken(newToken: string): void {
    token.value = newToken
    localStorage.setItem(TOKEN_KEY, newToken)
  }

  function setUser(newUser: User): void {
    user.value = newUser
    localStorage.setItem(USER_KEY, JSON.stringify(newUser))
  }

  // Accetta anche undefined: una risposta di login servita da un backend che non espone
  // ancora `timeZone` non deve scrivere la stringa "undefined" in localStorage.
  function setTimeZone(newTimeZone: string | null | undefined): void {
    if (!newTimeZone) {
      timeZone.value = null
      localStorage.removeItem(TIME_ZONE_KEY)
      return
    }
    timeZone.value = newTimeZone
    localStorage.setItem(TIME_ZONE_KEY, newTimeZone)
  }

  function logout(): void {
    user.value = null
    token.value = null
    timeZone.value = null
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    localStorage.removeItem(TIME_ZONE_KEY)
    // Il resto dei dati di lavoro non è meno personale del token: su una postazione
    // condivisa resterebbe a disposizione di chi entra dopo.
    clearUserScopedStorage()
  }

  /**
   * Chiude la sessione se il token è scaduto. La chiama la guardia del router a OGNI
   * navigazione e non solo all'avvio: una scheda lasciata aperta la sera ha ancora il token
   * in memoria la mattina dopo, e il primo clic deve portare al login, non a una pagina che
   * si svuota da sola.
   */
  function dropExpiredSession(): void {
    if (token.value !== null && isTokenExpired(token.value)) {
      logout()
    }
  }

  return {
    user,
    token,
    timeZone,
    isAuthenticated,
    setToken,
    setUser,
    setTimeZone,
    logout,
    dropExpiredSession,
  }
})
